/**
 * gfe-h5tabs.js
 * Gears For Ears — Maranello theme
 *
 * Splits the product description into Maranello tabs. Originally this split on
 * <h5> boundaries only, but product descriptions in the catalog use INCONSISTENT
 * heading tags (some use <h2> for "Specifications", others <h5>), so a tag-only
 * rule misses many products.
 *
 * NEW BEHAVIOUR (tag-agnostic, text-driven):
 *   - We look at heading elements h2–h5 anywhere in the description.
 *   - A heading becomes a NEW TAB only if its trimmed text matches one of the
 *     TAB_SECTION_NAMES below (case-insensitive). This avoids fragmenting
 *     descriptions on every sub-feature <h3> or on the main intro <h2>.
 *   - Everything up to the first matched heading stays in the Description tab.
 *   - Content from a matched heading runs until the next matched heading (or end).
 *
 * To add/remove which sections get their own tab, edit TAB_SECTION_NAMES.
 *
 * Relies on Maranello's TabsNavigation / TabsComponent custom elements
 * (assets/scripts.js ~1925–1991).
 */
(function () {
  'use strict';

  // Heading texts (case-insensitive, trimmed) that should become their own tab.
  // Add variants here as your content uses them.
  var TAB_SECTION_NAMES = [
    'specifications',
    'specification',
    'tech specs',
    'technical specifications',
    'technical specification',
    'package contents',
    'package content',
    "what's in the box",
    'what’s in the box',
    'in the box',
    'box contents'
  ];

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function isTabHeading(el) {
    return TAB_SECTION_NAMES.indexOf(normalize(el.textContent)) !== -1;
  }

  /**
   * Find heading elements (h2–h5) whose text matches TAB_SECTION_NAMES, then use
   * the Range API to extract the content slices between them.
   *
   * Returns an array of sections:
   *   sections[0]      -> { title: null,   fragment }  (everything before the first match)
   *   sections[1..n]   -> { title: string, fragment }  (each matched section)
   * Returns null when no matching heading is found.
   */
  function splitRteOnSections(rte) {
    var headings = Array.from(rte.querySelectorAll('h2, h3, h4, h5')).filter(function (h) {
      return h.textContent.trim() !== '' && isTabHeading(h);
    });
    if (headings.length === 0) return null;

    var sections = [];

    // Section 0: everything before the first matched heading
    var preRange = document.createRange();
    preRange.setStart(rte, 0);
    preRange.setEndBefore(headings[0]);
    sections.push({ title: null, fragment: preRange.cloneContents() });

    // Sections 1..n: from each matched heading to the next match (or end of rte)
    for (var i = 0; i < headings.length; i++) {
      var range = document.createRange();
      range.setStartAfter(headings[i]);
      if (i + 1 < headings.length) {
        range.setEndBefore(headings[i + 1]);
      } else {
        range.setEnd(rte, rte.childNodes.length);
      }
      sections.push({
        title: headings[i].textContent.trim(),
        fragment: range.cloneContents()
      });
    }

    return sections;
  }

  function panelHasTabHeading(panel) {
    var headings = panel.querySelectorAll('.rte h2, .rte h3, .rte h4, .rte h5');
    for (var i = 0; i < headings.length; i++) {
      if (isTabHeading(headings[i])) return true;
    }
    return false;
  }

  function initTabs() {
    // Only run on product pages
    if (!document.body.classList.contains('template-product')) return;

    document.querySelectorAll('tabs-component').forEach(function (tabsComponent) {
      var tabsNav = document.querySelector(
        'tabs-navigation[for="' + tabsComponent.id + '"]'
      );
      if (!tabsNav || typeof tabsNav.setActiveTab !== 'function') return;

      var tabNav = tabsNav.querySelector('.tab-nav');
      if (!tabNav) return;

      // Find the first content panel whose .rte contains a matching heading
      var descContent = null;
      tabsComponent.querySelectorAll('.tabs-component-content').forEach(function (panel) {
        if (!descContent && panelHasTabHeading(panel)) {
          descContent = panel;
        }
      });
      if (!descContent) return;

      var rte = descContent.querySelector('.rte');
      var descHandle = descContent.getAttribute('data-handle');
      var descNavItem = tabNav.querySelector('[data-handle="' + descHandle + '"]');

      var sections = splitRteOnSections(rte);
      if (!sections || sections.length <= 1) return;

      // Rebuild description tab with pre-section content only
      while (rte.firstChild) rte.removeChild(rte.firstChild);
      rte.appendChild(sections[0].fragment);

      // Hide description tab if it is now empty
      var descIsEmpty = rte.textContent.trim() === '';
      if (descIsEmpty && descNavItem) {
        descNavItem.classList.add('hidden');
      }

      // Create a tab for each matched section
      var newNavItems = [];
      var insertAfterNav = descNavItem;

      sections.slice(1).forEach(function (section, index) {
        if (!section.title) return;

        var handle = descHandle + '-sec-' + index;

        var navItem = document.createElement('a');
        navItem.href = '#';
        navItem.className = 'tab-nav-item';
        navItem.setAttribute('data-handle', handle);
        navItem.textContent = section.title;

        if (insertAfterNav && insertAfterNav.parentNode) {
          insertAfterNav.parentNode.insertBefore(navItem, insertAfterNav.nextSibling);
        } else {
          tabNav.appendChild(navItem);
        }
        insertAfterNav = navItem;
        newNavItems.push(navItem);

        var panel = document.createElement('div');
        panel.className = 'tabs-component-content background-complementary';
        panel.setAttribute('data-handle', handle);
        panel.setAttribute('aria-hidden', 'true');

        var innerRte = document.createElement('div');
        innerRte.className = 'rte';
        innerRte.appendChild(section.fragment);
        panel.appendChild(innerRte);
        tabsComponent.appendChild(panel);
      });

      // Re-sync TabsNavigation.navs to include the new items
      tabsNav.navs = tabsNav.querySelectorAll('a:not(.hidden), button');

      // Wire click events for new nav items
      newNavItems.forEach(function (navItem) {
        navItem.addEventListener('click', function (e) {
          e.preventDefault();
          tabsNav.setActiveTab(navItem);
        });
      });

      // Activate first new tab when description was emptied
      if (descIsEmpty && newNavItems.length > 0) {
        tabNav.querySelectorAll('.tab-nav-item').forEach(function (item) {
          item.classList.remove('is-active');
        });
        tabsNav.setActiveTab(newNavItems[0]);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabs);
  } else {
    initTabs();
  }
})();
